/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { isEqual } from 'lodash';
import Permissions from 'permissions/permissions';
import RolesUtil from 'permissions/utils/roles';
import { connect, useDispatch } from 'react-redux';
import TieoutElement, {
  onElementClick,
} from 'components/feature/statement-content-panel/tieout-element-component';
import {
  setSourceStatementElementMapAction,
  setTargetStatementElementMapAction,
  setSourceStatementElementMapForAutoTieout,
  setTargetStatementElementMapForAutoTieout,
} from 'store/actions/side-by-side-statement/side-by-side-statement-actions';
import { ROUTE_CONSTANTS } from 'constants/util/route-constants';
import {
  getExcessiveElementSelectionNotification,
  handleShowCrossIconByStmtWrappedUp,
  handleShowSelectionCursorForMappedElements,
} from 'constants/feature/tieout-element-utils';
import { updateSideBySideElementCacheByCallouts } from 'store/actions/statement-content-annotation-creation-actions';
import {
  AUTO_TIEOUT_COMPARE_MODE,
  SIDE_BY_SIDE_COMPARE_MODE,
  STATEMENT_ELEMENT_ID_PREFIX,
  STATEMENT_TYPES,
  TRAIL_BALANCE_MODE,
} from 'constants/feature/statement-content-constants';
import { ElementSelectionLimit } from 'constants/feature/tieout-element-constants';
import { isNullOrUndefined } from 'utils/object-utils';
import { performManualMappingInAutoTieout } from 'store/actions/statement-content-actions';
import { handleAutoTieoutElementClick as autoTieoutElementClick } from 'utils/auto-tieout-element-utils';
import {
  clearSelectedElementsContentPanel,
  clearBatchSelectedElementsAction,
  clearSideBySideElementsMap,
} from 'store/actions/action-creators';
import { clearLeftSelectedElementsContentPanel } from 'store/actions/statement-content-actions';
import {
  removeTargetElementClicked,
  removeSourceElementClicked,
} from 'store/actions/auto-tieout/mapped-elements-actions';
import {
  removeTBSourceElementClicked,
  setTBTargetElementClicked,
  removeTBTargetElementClicked,
} from 'store/actions/trial-balance/mapped-element-action';
import {
  performManualMappingInTrialBalance,
  setElementToScrollInTrialBalanceView,
  clearTrialBalanceRowHighlight,
} from 'store/actions/trial-balance/trial-balance-action';
import { setTargetStatementElementMapForTrialBalance } from 'store/actions/side-by-side-statement/side-by-side-statement-actions';
import { isModeForAddCursor } from 'utils/modes-utils';
import { ELEMENT_SELECT_MODES } from 'constants/feature/modes-constants';
import { INCOME_STATEMENT_WORKPAPER } from 'constants/feature/element-panel-constants';

export const TieoutElementHOC = (props) => {
  const {
    elementData,
    leftSideView,
    elementDataparts,
    stateData,
    sideBySideViewMode: { viewMode },
    leftSectionIdList,
    autoTieoutElementData,
    leftSectionsInView,
    selectedStatementIsOCR,
    leftStatementIsOCR,
    sectionsInView,
    isReadOnlyStatement,
    zoom,
    rightZoom,
    tbWorkFlowStatus,
    trialBalanceMappedElements,
    isSnapshotViewLoaded,
    isStatementWrappedOrPendingApproval,
    isReadOnlyOverride,
    isSnapshotReadOnly,
    highlightMappedElements,
    selectMode,
    editPermissionAutotieout,
  } = props;
  // on page load, store the props in a ref.
  const latestProps = useRef(props);
  const isSideBySideMode = useRef(false);
  const isAutoTieoutMode = useRef(false);
  const isTrialBalanceMode = useRef(false);
  const [menuOptions, setMenuOptions] = useState([]);

  useEffect(() => {
    // update the ref with the latest props. based on the on change of props
    latestProps.current = props;
  }, [props]);

  const pathName = useLocation().pathname;
  const { projectId, statementId, revisionId } = useParams();
  const dispatch = useDispatch();

  const isObserverRole = RolesUtil.doesUserHaveObserverRoleForProject(
    stateData,
    projectId,
  );

  const hasPermission = useMemo(
    () =>
      Permissions.SideBySideStatementViewPermissions.canSelectElement(
        stateData,
        projectId,
      ),
    [projectId],
  );

  const _isSideBySideView = useMemo(
    () => pathName.includes(ROUTE_CONSTANTS.SIDE_BY_SIDE),
    [pathName],
  );

  const zoomValue =
    _isSideBySideView && !leftSideView ? rightZoom?.zoomRatio : zoom?.zoomRatio;

  useMemo(() => {
    isSideBySideMode.current =
      _isSideBySideView && viewMode === SIDE_BY_SIDE_COMPARE_MODE;
    isAutoTieoutMode.current =
      _isSideBySideView && viewMode === AUTO_TIEOUT_COMPARE_MODE;
    isTrialBalanceMode.current =
      _isSideBySideView && viewMode === TRAIL_BALANCE_MODE;
  }, [viewMode]);

  const getCountForSelectedElement = useCallback(() => {
    const elementCount =
      props.sideBySideElementMap &&
      props.sideBySideElementMap.getCountOfElementsSelected();
    return elementCount + 1;
  }, [props.sideBySideElementMap]);

  //Hiding unlink WP icon for Wrapped up statements or snapshot read only or observer role
  const handleCrossIconByStmtWrappedUpState = () => {
    return handleShowCrossIconByStmtWrappedUp({
      isStatementWrappedOrPendingApproval:
        isStatementWrappedOrPendingApproval ||
        isSnapshotReadOnly ||
        isObserverRole,
      isTrialBalanceMode: isTrialBalanceMode.current,
      workFlowStatus: isAutoTieoutMode.current
        ? latestProps.current.workFlowStatus
        : latestProps.current.tbWorkFlowStatus,
      isAutoTieoutMode: isAutoTieoutMode.current,
      leftView: leftSideView,
      mappedElements: isTrialBalanceMode.current
        ? trialBalanceMappedElements
        : autoTieoutElementData,
      elementDetails: elementData,
      isReadOnlyOverride,
      editPermissionAutotieout,
    });
  };

  //Handle cursor when unlink WP icon for Wrapped up statements or snapshot read only or observer role
  const handleCursorByStmtWrappedUpState = () => {
    return handleShowSelectionCursorForMappedElements({
      isStatementWrappedOrPendingApproval:
        isStatementWrappedOrPendingApproval ||
        isSnapshotReadOnly ||
        isReadOnlyOverride ||
        isObserverRole,
      isTrialBalanceMode: isTrialBalanceMode.current,
      workFlowStatus: isAutoTieoutMode.current
        ? latestProps.current.workFlowStatus
        : latestProps.current.tbWorkFlowStatus,
      isAutoTieoutMode: isAutoTieoutMode.current,
      leftView: leftSideView,
      mappedElements: isTrialBalanceMode.current
        ? trialBalanceMappedElements
        : autoTieoutElementData,
      elementDetails: elementData,
    });
  };

  const handleAutoTieoutElementClick = useCallback(
    ({ elementId }) => {
      const { workFlowStatus } = latestProps.current;
      if (
        isStatementWrappedOrPendingApproval &&
        workFlowStatus.isCompleted &&
        !leftSideView &&
        !autoTieoutElementData.isTargetElementActive(elementId)
      ) {
        return;
      }
      //clear the element selection if any on content panel
      dispatch(clearSelectedElementsContentPanel());
      dispatch(clearLeftSelectedElementsContentPanel());
      const isAutoTieoutMappingFound = leftSideView
        ? autoTieoutElementData.getTargetElementForSource(elementId)
        : autoTieoutElementData.getSourceElementForTarget(elementId);
      if (!isNullOrUndefined(isAutoTieoutMappingFound)) {
        dispatch(clearSideBySideElementsMap());
        autoTieoutElementClick({
          elementId,
          isLeftView: leftSideView,
          isAutoTieoutMode: isAutoTieoutMode.current,
          autoTieoutElementData,
          sectionsInView,
          leftSectionsInView,
          selectedStatementIsOCR,
          leftStatementIsOCR,
          dispatch,
        });
      } else if (
        !isObserverRole &&
        !isReadOnlyOverride &&
        editPermissionAutotieout
      ) {
        dispatch(removeSourceElementClicked());
        dispatch(removeTargetElementClicked());

        const { workFlowStatus, elementData, leftSectionIdList } =
          latestProps.current;
        let elementDataFromCache = elementData.data;
        const targetStatementParams = {
          projectId,
          statementId,
          revisionId,
        };
        const isLeftSectionLoaded = leftSectionIdList.isLoaded;
        if (
          elementDataFromCache &&
          isLeftSectionLoaded &&
          workFlowStatus.isCompleted
        ) {
          if (leftSideView) {
            dispatch(
              setSourceStatementElementMapForAutoTieout(elementDataFromCache),
            );
            dispatch(
              performManualMappingInAutoTieout({
                sideBySideElementMap: latestProps.current.sideBySideElementMap,
                sourceStatementParams:
                  latestProps.current.sourceStatementParams,
                targetStatementParams,
              }),
            );
          } else if (!leftSideView) {
            if (isTrialBalanceMode.current) {
              return;
            }
            dispatch(
              setTargetStatementElementMapForAutoTieout(elementDataFromCache),
            );
            dispatch(
              performManualMappingInAutoTieout({
                sideBySideElementMap: latestProps.current.sideBySideElementMap,
                sourceStatementParams:
                  latestProps.current.sourceStatementParams,
                targetStatementParams,
              }),
            );
          }
        }
      }
    },
    [
      sectionsInView,
      isAutoTieoutMode,
      leftSideView,
      autoTieoutElementData,
      leftSectionsInView,
      selectedStatementIsOCR,
      leftStatementIsOCR,
      props.sideBySideElementMap,
      dispatch,
    ],
  );

  const handleTrialBalanceElementClick = useCallback(
    ({ elementId }) => {
      if (
        isStatementWrappedOrPendingApproval &&
        tbWorkFlowStatus.isCompleted &&
        !leftSideView &&
        !trialBalanceMappedElements.isElementExistsInTargetMap(elementId)
      ) {
        return;
      }

      if (!isSnapshotViewLoaded) {
        return;
      }
      dispatch(clearSelectedElementsContentPanel());
      dispatch(clearBatchSelectedElementsAction());
      dispatch(clearTrialBalanceRowHighlight());

      const existingMapping =
        trialBalanceMappedElements?.getTargetMapping?.(elementId);
      const isElementAlreadyMapped =
        !isNullOrUndefined(existingMapping) && existingMapping.active;

      if (isElementAlreadyMapped) {
        const targetElementClicked =
          trialBalanceMappedElements?.getTargetElementClicked?.();
        const elementClickedId = targetElementClicked?.elementId;

        if (
          !isNullOrUndefined(elementClickedId) &&
          elementId == elementClickedId
        ) {
          dispatch(removeTBSourceElementClicked());
          dispatch(removeTBTargetElementClicked());
          return;
        }

        dispatch(clearSideBySideElementsMap());
        dispatch(removeTBSourceElementClicked());
        dispatch(setTBTargetElementClicked(existingMapping));
        const elementRowId = elementId && existingMapping?.tbDataId;
        dispatch(
          setElementToScrollInTrialBalanceView({
            elementId,
            rowId: elementRowId,
            tbWorkPaperType:
              elementData?.getTrialBalanceWorkpaperType() ===
              INCOME_STATEMENT_WORKPAPER
                ? STATEMENT_TYPES.INCOME_STATEMENT
                : STATEMENT_TYPES.BALANCE_SHEET,
          }),
        );
      } else if (
        !isSnapshotReadOnly &&
        !isObserverRole &&
        !isReadOnlyOverride &&
        editPermissionAutotieout &&
        tbWorkFlowStatus.isCompleted
      ) {
        dispatch(removeTBSourceElementClicked());
        dispatch(removeTBTargetElementClicked());

        const elementDataFromCache = latestProps.current.elementData?.data;
        const statementParams = { projectId, statementId, revisionId };

        if (elementDataFromCache) {
          dispatch(
            setTargetStatementElementMapForTrialBalance(elementDataFromCache),
          );

          dispatch(
            performManualMappingInTrialBalance({
              statementParams,
            }),
          );
        }
      }
    },
    [
      trialBalanceMappedElements,
      projectId,
      statementId,
      revisionId,
      dispatch,
      isSnapshotViewLoaded,
      isSnapshotReadOnly,
      isObserverRole,
    ],
  );

  const handleElementClick = useCallback(
    ({ elementId, sectionId, isDisabled }) => {
      const currentSelectMode = latestProps.current.selectMode;
      if (_isSideBySideView) {
        // Prevent batch operations for left side view in trial balance and auto tieout modes
        const isBatchMode =
          currentSelectMode === ELEMENT_SELECT_MODES.BATCH ||
          currentSelectMode === ELEMENT_SELECT_MODES.BATCH_WITH_BANNER;
        if (
          leftSideView &&
          (isTrialBalanceMode.current || isAutoTieoutMode.current) &&
          isBatchMode
        ) {
          return;
        }
        if (
          (isTrialBalanceMode.current || isAutoTieoutMode.current) &&
          isModeForAddCursor(currentSelectMode)
        ) {
          return dispatch(onElementClick({ elementId, sectionId, isDisabled }));
        } else if (isSideBySideMode.current && hasPermission) {
          let elementDataFromCache = !isNullOrUndefined(elementData)
            ? elementData.data
            : latestProps.current.elementData &&
              latestProps.current.elementData.data;
          if (getCountForSelectedElement() <= ElementSelectionLimit) {
            if (leftSideView)
              elementDataFromCache &&
                dispatch(
                  setSourceStatementElementMapAction(elementDataFromCache),
                );
            else
              _shouldEnableTargetElementForSelection(elementId) &&
                elementDataFromCache &&
                dispatch(
                  setTargetStatementElementMapAction(elementDataFromCache),
                );
          } else {
            if (
              leftSideView &&
              props.sideBySideElementMap.getSelectedSourceElementIfExists(
                parseInt(
                  elementDataFromCache?.id
                    ? elementDataFromCache.id
                    : elementId,
                ),
              ) // check if elementId is present in source elements id
            ) {
              elementDataFromCache &&
                dispatch(
                  setSourceStatementElementMapAction(elementDataFromCache),
                );
              return;
            } else if (
              !leftSideView &&
              props.sideBySideElementMap.getSelectedTargetElementIfExists(
                parseInt(
                  elementDataFromCache?.id
                    ? elementDataFromCache.id
                    : elementId,
                ),
              ) // check if elementId is present in target elements id
            ) {
              _shouldEnableTargetElementForSelection(elementId) &&
                elementDataFromCache &&
                dispatch(
                  setTargetStatementElementMapAction(elementDataFromCache),
                );
              return;
            } else {
              getExcessiveElementSelectionNotification(ElementSelectionLimit);
            }
          }
        }
      } else if (!_isSideBySideView) {
        return dispatch(onElementClick({ elementId, sectionId, isDisabled }));
      }
    },
    [
      pathName,
      hasPermission,
      leftSideView,
      _isSideBySideView,
      isSideBySideMode.current,
      viewMode,
      latestProps.current.sideBySideElementMap,
      latestProps.current.selectMode,
      latestProps.current.isSnapshotViewLoaded,
    ],
  );

  const handleAnnotationClick = useCallback(
    ({ elementId, showCallout }) => {
      dispatch(
        updateSideBySideElementCacheByCallouts({
          elementIds: [elementId],
          showCallout,
          isLeftSideView: leftSideView,
        }),
      );
    },
    [leftSideView],
  );

  const isElementSelectedForSideBySideMap = useCallback(
    (elementId) => {
      if (_isSideBySideView) {
        if (leftSideView) {
          return (
            props.sideBySideElementMap &&
            props.sideBySideElementMap.getSourceElementMapByElementId(elementId)
          );
        }
        return (
          props.sideBySideElementMap &&
          props.sideBySideElementMap.getTargetElementMapByElementId(elementId)
        );
      } else return false;
    },
    [_isSideBySideView, leftSideView, props.sideBySideElementMap],
  );

  const getCountForSelectedElementForSideBySideMapping = useCallback(
    (elementId) => {
      if (
        (_isSideBySideView && isSideBySideMode.current) ||
        isAutoTieoutMode.current ||
        isTrialBalanceMode.current
      ) {
        if (leftSideView) {
          return (
            props.sideBySideElementMap &&
            props.sideBySideElementMap.getCountForMappedSourceElement(elementId)
          );
        }
        return (
          props.sideBySideElementMap &&
          props.sideBySideElementMap.getCountForMappedTargetElement(elementId)
        );
      } else return null;
    },
    [
      isSideBySideMode.current,
      isAutoTieoutMode.current,
      isTrialBalanceMode.current,
      leftSideView,
      props.sideBySideElementMap,
    ],
  );

  const _shouldEnableTargetElementForSelection = useCallback(
    (elementId) => {
      if (_isSideBySideView) {
        if (
          isSideBySideMode.current &&
          !leftSideView &&
          latestProps.current.sideBySideElementMap
        ) {
          return (
            latestProps.current.sideBySideElementMap
              .mapKeyOfFirstUnmappedTargetElement ||
            latestProps.current.sideBySideElementMap.getTargetElementMapByElementId(
              elementId,
            )
          );
        }
      }
    },
    [
      isSideBySideMode.current,
      leftSideView,
      latestProps.current.sideBySideElementMap,
    ],
  );

  const handleSideBySideIsDisabled = useCallback(
    (elementId) => {
      if (_isSideBySideView) {
        if (isSideBySideMode.current) {
          return leftSideView
            ? !hasPermission
            : !(
                hasPermission &&
                _shouldEnableTargetElementForSelection(elementId)
              );
        } else if (isAutoTieoutMode.current) {
          if (!leftSideView && isModeForAddCursor(selectMode)) {
            return false;
          }
          return !leftSectionIdList.isLoaded;
        } else if (isTrialBalanceMode.current) {
          if (leftSideView) {
            return true;
          }
          return false;
        } else {
          return true;
        }
      }
    },
    [
      leftSideView,
      leftSectionIdList.isLoaded,
      isSideBySideMode.current,
      isAutoTieoutMode.current,
      isTrialBalanceMode.current,
      tbWorkFlowStatus,
      selectMode,
    ],
  );

  const isCursorAlias = useMemo(() => {
    // Show cursor alias for right side view in trial balance / auto tieout modes when in batch mode
    if (
      !leftSideView &&
      (isTrialBalanceMode.current || isAutoTieoutMode.current) &&
      isModeForAddCursor(selectMode)
    ) {
      return true;
    }
    if (isSideBySideMode.current) {
      const isCursorAliasOnRightView =
        latestProps.current.sideBySideElementMap.sizeOfSourceMapping >
        latestProps.current.sideBySideElementMap.sizeOfTargetMapping;
      const isCursorAliasView = leftSideView ? true : isCursorAliasOnRightView;
      return hasPermission // return true if user has permission to the respective statement for elements mapping in side-by-side view
        ? isCursorAliasView // returns true if it is left side statement in side-by-side view or number of selected source elements is greater than number of selected target elements to change the pointer(disabled to selection pointer) of right statement accordingly in side-by-side view
        : false;
    }
    return false;
  }, [
    leftSideView,
    hasPermission,
    isSideBySideMode.current,
    isTrialBalanceMode.current,
    isAutoTieoutMode.current,
    selectMode,
    latestProps.current.numberOfSourceElementSelected,
    latestProps.current.numberOfTargetElementSelected,
  ]);

  return (
    <TieoutElement
      {...props}
      isCursorAlias={isCursorAlias}
      isSideBySideViewMode={isSideBySideMode.current} // true for side-by-side view
      isSideBySideView={_isSideBySideView} // true for both side-by-side/auto-tieout view
      isAutoTieoutMode={isAutoTieoutMode.current} // true for auto-tieout view
      isTrialBalanceMode={isTrialBalanceMode.current} // true for trial balance view
      workFlowStatus={
        isTrialBalanceMode.current
          ? latestProps.current.tbWorkFlowStatus
          : latestProps.current.workFlowStatus
      }
      tbWorkFlowStatus={tbWorkFlowStatus}
      isSideBySideViewDisabled={handleSideBySideIsDisabled}
      elementDataparts={elementDataparts}
      elementData={elementData}
      handleElementClick={handleElementClick}
      handleAnnotationClick={handleAnnotationClick}
      isElementSelectedForSideBySideMap={isElementSelectedForSideBySideMap}
      getCountForSelectedElementForSideBySideMapping={
        getCountForSelectedElementForSideBySideMapping
      }
      handleAutoTieoutElementClick={handleAutoTieoutElementClick}
      handleTrialBalanceElementClick={handleTrialBalanceElementClick}
      autoTieoutElementData={autoTieoutElementData}
      trialBalanceMappedElements={trialBalanceMappedElements}
      isReadOnlyStatement={isReadOnlyStatement}
      setMenuOptions={setMenuOptions}
      menuOptions={menuOptions}
      zoomRatio={zoomValue}
      viewMode={viewMode}
      isStatementWrappedOrPendingApproval={isStatementWrappedOrPendingApproval}
      isSnapshotReadOnly={isSnapshotReadOnly}
      showSelectionPointerForMappedElements={handleCursorByStmtWrappedUpState()}
      showCrossIconByStmtWrappedUpState={handleCrossIconByStmtWrappedUpState()}
      highlightMappedElements={_isSideBySideView && highlightMappedElements}
      editPermissionAutotieout={editPermissionAutotieout}
    />
  );
};
const mapStateToProps = (
  {
    data: {
      currentUser,
      projectList,
      selectedProject,
      batchPanel: { batchElementList },
      elementPanel: {
        elementDetails: { id: elementPanelElementId },
      },
      copyFormula: { tableMatrix },
      annotationDisplayOptions: annotationDisplayFilter,
      selectedStatement: { isCftEnabled, isOCR: selectedStatementIsOCR },
      selectedStatement,
      leftSelectedStatement: { isOCR: leftStatementIsOCR },
      statementContent: { elementsDataparts, elementCache },
      leftStatementContent: {
        leftElementsDataparts,
        leftElementCache,
        leftSectionIdList,
      },
      statementSummary: {
        annotationPosition,
        statementSummaryElementsDisplay: {
          displayElementDetailsOnStatementPage,
        },
      },
      editPermission: { editPermissionAutotieout },
    },
    ui: {
      sourceStatementParams,
      sideBySideView: {
        sideBySideElementMap,
        sideBySideViewMode,
        shouldHighlightMappedElements: { highlightMappedElements },
        autoTieout: {
          mappedElements: autoTieoutElementData,
          workFlowStatus,
          workpaperDetails,
        },
        trialBalance: {
          tbWorkFlowStatus,
          mappedElements: trialBalanceMappedElements,
          isSnapshotViewLoaded,
          isSnapshotReadOnly,
        },
      },
      statementPage: {
        selectedElementsMap,
        leftSelectedElementsMap,
        panels: { right },
        highlightedElementId,
        leftSectionsInView,
        sectionsInView,
        modes: { selectMode },
        zoom,
        rightZoom,
      },
    },
  },
  ownProps,
) => {
  let elementId = ownProps.id.substring(STATEMENT_ELEMENT_ID_PREFIX.length);
  if (elementId && elementId.indexOf('_') >= 0) {
    elementId = elementId.substr(0, elementId.indexOf('_'));
  }
  const sectionId = ownProps.sectionId;
  const elementData = ownProps.leftSideView
    ? leftElementCache.getElement({ elementId, sectionId: sectionId })
    : elementCache.getElement({ elementId, sectionId: sectionId });

  const isElementSelected = ownProps.leftSideView
    ? leftSelectedElementsMap.has(elementId)
    : selectedElementsMap.has(elementId);
  const elementHighlightState = ownProps.leftSideView
    ? leftSelectedElementsMap.getElementHighlightColor(elementId)
    : selectedElementsMap.getElementHighlightColor(elementId);

  const rightPanelState =
    parseInt(elementId) === elementPanelElementId ? right : null;

  let isPartOfTableMatrix = false;
  if (tableMatrix.length > 0) {
    tableMatrix[elementId]
      ? (isPartOfTableMatrix = true)
      : (isPartOfTableMatrix = false);
  } else {
    isPartOfTableMatrix = true;
  }

  const isStatementWrappedOrPendingApproval =
    selectedStatement.isStatementWrappedOrPendingApproval();

  const isReadOnlyStatement = selectedStatement.isReadOnly();
  const isReadOnlyOverride = selectedStatement?.data?.readOnlyOverride;

  const elementDataparts = ownProps.leftSideView
    ? leftElementsDataparts
    : elementsDataparts;

  const renderElementDataParts =
    elementDataparts &&
    elementDataparts.elements &&
    !!Object.keys(elementDataparts.elements).length &&
    elementId in elementDataparts.elements &&
    !isNullOrUndefined(elementDataparts.elements[elementId]);

  return {
    isElementSelected,
    elementHighlightState,
    elementData,
    annotationDisplayFilter,
    isCftEnabled,
    isPartOfTableMatrix,
    elementPanelElementId: elementPanelElementId,
    highlightedElementId,
    rightPanelState,
    annotationPosition,
    elementDataparts,
    renderElementDataParts,
    sideBySideElementMap,
    numberOfSourceElementSelected:
      sideBySideElementMap && sideBySideElementMap.sizeOfSourceMapping,
    numberOfTargetElementSelected:
      sideBySideElementMap && sideBySideElementMap.sizeOfTargetMapping,
    displayElementDetailsOnStatementPage,
    sectionId,
    sideBySideViewMode,
    workFlowStatus,
    tbWorkFlowStatus,
    isSnapshotReadOnly,
    workpaperDetails,
    stateData: { currentUser, projectList, selectedProject },
    leftSectionIdList,
    sourceStatementParams,
    autoTieoutElementData,
    leftSectionsInView,
    selectedStatementIsOCR,
    leftStatementIsOCR,
    sectionsInView,
    isReadOnlyStatement,
    isStatementWrappedOrPendingApproval,
    isReadOnlyOverride,
    selectMode,
    zoom,
    rightZoom,
    trialBalanceMappedElements,
    isSnapshotViewLoaded,
    highlightMappedElements,
    batchElementList,
    editPermissionAutotieout,
  };
};

// Custom comparison function for React.memo
const areEqual = (prevProps, nextProps) => {
  // For example, only re-render if `elementData` or `leftSideView` changes
  return (
    prevProps.currentUser === nextProps.currentUser &&
    prevProps.projectList === nextProps.projectList &&
    prevProps.selectedProject === nextProps.selectedProject &&
    prevProps.elementData === nextProps.elementData &&
    prevProps.leftSideView === nextProps.leftSideView &&
    prevProps.sideBySideElementMap === nextProps.sideBySideElementMap &&
    prevProps.leftSelectedElementsMap === nextProps.leftSelectedElementsMap &&
    prevProps.isElementSelected === nextProps.isElementSelected && // updating selectedElementsMap use in tieout element
    prevProps.elementHighlightState === nextProps.elementHighlightState && // updating selectedElementsMap use in tieout element
    prevProps.numberOfSourceElementSelected ===
      nextProps.numberOfSourceElementSelected &&
    prevProps.numberOfTargetElementSelected ===
      nextProps.numberOfTargetElementSelected &&
    prevProps.renderElementDataParts === nextProps.renderElementDataParts && // To render when change in elementDataparts
    prevProps.annotationDisplayFilter === nextProps.annotationDisplayFilter &&
    prevProps.isCftEnabled === nextProps.isCftEnabled &&
    prevProps.isPartOfTableMatrix === nextProps.isPartOfTableMatrix &&
    prevProps.highlightedElementId === nextProps.highlightedElementId &&
    prevProps.rightPanelState === nextProps.rightPanelState &&
    prevProps.annotationPosition.position ===
      nextProps.annotationPosition.position &&
    prevProps.displayElementDetailsOnStatementPage ===
      nextProps.displayElementDetailsOnStatementPage &&
    prevProps.sideBySideViewMode.viewMode ===
      nextProps.sideBySideViewMode.viewMode &&
    prevProps.workFlowStatus === nextProps.workFlowStatus &&
    prevProps.sourceStatementParams.projectId ===
      nextProps.sourceStatementParams.projectId &&
    prevProps.sourceStatementParams.statementId ===
      nextProps.sourceStatementParams.statementId &&
    prevProps.sourceStatementParams.revisionId ===
      nextProps.sourceStatementParams.revisionId &&
    prevProps.leftSectionIdList.isLoaded ===
      nextProps.leftSectionIdList.isLoaded &&
    isEqual(prevProps.autoTieoutElementData, nextProps.autoTieoutElementData) &&
    prevProps.isReadOnlyStatement === nextProps.isReadOnlyStatement &&
    isEqual(prevProps.selectMode, nextProps.selectMode) &&
    prevProps.zoom === nextProps.zoom &&
    prevProps.rightZoom === nextProps.rightZoom &&
    prevProps.tbWorkFlowStatus === nextProps.tbWorkFlowStatus &&
    isEqual(
      prevProps.trialBalanceMappedElements,
      nextProps.trialBalanceMappedElements,
    ) &&
    prevProps.isSnapshotViewLoaded === nextProps.isSnapshotViewLoaded &&
    prevProps.isStatementWrappedOrPendingApproval ===
      nextProps.isStatementWrappedOrPendingApproval &&
    prevProps.isSnapshotReadOnly === nextProps.isSnapshotReadOnly &&
    prevProps.isReadOnlyOverride === nextProps.isReadOnlyOverride &&
    prevProps.highlightMappedElements === nextProps.highlightMappedElements &&
    isEqual(prevProps.batchElementList, nextProps.batchElementList) &&
    prevProps.editPermissionAutotieout === nextProps.editPermissionAutotieout
  );
};

export default connect(
  mapStateToProps,
  null,
)(React.memo(TieoutElementHOC, areEqual));
